import React, { useState } from 'react';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Switch, 
  Button, 
  Divider, 
  Space, 
  Row, 
  Col,
  Select,
  InputNumber,
  Alert
} from 'antd';
import { 
  SettingOutlined, 
  SaveOutlined, 
  ReloadOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  ApiOutlined,
  MailOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SystemSettings = () => {
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setHasChanges(false);
      console.log('Settings saved:', values);
    }, 1000);
  };

  const handleReset = () => {
    form.resetFields();
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
              <SettingOutlined className="text-xl text-white" />
            </div>
            <div>
              <Title level={3} className="mb-1">System Settings</Title>
              <Text className="text-gray-600">Configure system-wide settings and preferences</Text>
            </div>
          </div>
          <Button icon={<ReloadOutlined />}>
            Reset to Defaults
          </Button>
        </div>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        initialValues={{
          siteName: 'Portfolio System',
          siteDescription: 'Professional portfolio management system',
          maintenanceMode: false,
          registrationEnabled: true,
          emailVerification: true,
          maxFileSize: 10,
          sessionTimeout: 24,
          apiRateLimit: 1000,
        }}
      >
        {/* General Settings */}
        <Card 
          title={
            <div className="flex items-center space-x-2">
              <SettingOutlined />
              <span>General Settings</span>
            </div>
          }
          className="shadow-soft border border-border-light rounded-xl"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Site Name"
                name="siteName"
                rules={[{ required: true, message: 'Please enter site name' }]}
              >
                <Input placeholder="Enter site name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Admin Email"
                name="adminEmail"
                rules={[
                  { required: true, message: 'Please enter admin email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="admin@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Site Description"
                name="siteDescription"
              >
                <TextArea rows={3} placeholder="Enter site description" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Security Settings */}
        <Card 
          title={
            <div className="flex items-center space-x-2">
              <SecurityScanOutlined />
              <span>Security Settings</span>
            </div>
          }
          className="shadow-soft border border-border-light rounded-xl"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Registration Enabled"
                name="registrationEnabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Email Verification Required"
                name="emailVerification"
                valuePropName="checked"
              >
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Session Timeout (hours)"
                name="sessionTimeout"
              >
                <InputNumber min={1} max={168} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Password Complexity"
                name="passwordComplexity"
              >
                <Select placeholder="Select complexity level">
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="medium">Medium</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* System Configuration */}
        <Card 
          title={
            <div className="flex items-center space-x-2">
              <DatabaseOutlined />
              <span>System Configuration</span>
            </div>
          }
          className="shadow-soft border border-border-light rounded-xl"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Maintenance Mode"
                name="maintenanceMode"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="On" 
                  unCheckedChildren="Off"
                  onChange={(checked) => {
                    if (checked) {
                      // Show warning
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Max File Upload Size (MB)"
                name="maxFileSize"
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="API Rate Limit (requests/hour)"
                name="apiRateLimit"
              >
                <InputNumber min={100} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Backup Frequency"
                name="backupFrequency"
              >
                <Select placeholder="Select backup frequency">
                  <Select.Option value="daily">Daily</Select.Option>
                  <Select.Option value="weekly">Weekly</Select.Option>
                  <Select.Option value="monthly">Monthly</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Email Settings */}
        <Card 
          title={
            <div className="flex items-center space-x-2">
              <MailOutlined />
              <span>Email Configuration</span>
            </div>
          }
          className="shadow-soft border border-border-light rounded-xl"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="SMTP Server"
                name="smtpServer"
              >
                <Input placeholder="smtp.example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="SMTP Port"
                name="smtpPort"
              >
                <InputNumber min={1} max={65535} placeholder="587" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="SMTP Username"
                name="smtpUsername"
              >
                <Input placeholder="your-email@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="SMTP Password"
                name="smtpPassword"
              >
                <Input.Password placeholder="Enter SMTP password" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Save Actions */}
        <Card className="shadow-soft border border-border-light rounded-xl">
          <div className="flex justify-between items-center">
            <div>
              {hasChanges && (
                <Text type="warning" className="text-sm">
                  You have unsaved changes
                </Text>
              )}
            </div>
            <Space>
              <Button onClick={handleReset} disabled={!hasChanges}>
                Reset
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={!hasChanges}
              >
                Save Settings
              </Button>
            </Space>
          </div>
        </Card>
      </Form>

      {/* Coming Soon Notice */}
      <Card className="shadow-soft border border-border-light rounded-xl bg-purple-50">
        <div className="text-center py-8">
          <SettingOutlined className="text-4xl text-purple-500 mb-4" />
          <Title level={4} className="text-purple-700 mb-2">Advanced System Settings Coming Soon</Title>
          <Text className="text-purple-600">
            Additional system configuration options including database settings, 
            logging configuration, and advanced security features will be available in future updates.
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default SystemSettings;